import {computed} from "vue";
import {cacheKey} from "@/lib/cache";
import useSwr from "@/core/composables/swr/useSwr";
import {bookingLinkPresetApi} from "@/container";
import {BookingLinkPresetIndex} from "@/core/http/resources/BookingLinkPresetResource";

/**
 * Saved booking links (« Liens pré-établis ») of the current organization, and
 * whether the pharmacy can share a link at all.
 *
 * A failed fetch is not fatal: the modal shows the saved tab in its degraded
 * state without blocking the composition of a new link.
 */
const useBookingLinkPresets = (organizationId: () => number | null) => {
    const key = computed(() => {
        if (!organizationId()) {
            return null;
        }

        return cacheKey(`organizations/${organizationId()}/appointment-link-presets`);
    });

    const fetchData = (): Promise<BookingLinkPresetIndex> => {
        const currentOrganizationId = organizationId();

        if (!currentOrganizationId) {
            return Promise.resolve({presets: [], bookingEnabled: false});
        }

        return bookingLinkPresetApi().index(currentOrganizationId);
    };

    const {data, error, isLoading, mutate} = useSwr(
        () => key.value,
        () => fetchData()
    );

    return {
        /** Undefined until the first response, which the modal reads as « unknown yet ». */
        data: computed(() => data.value?.presets),
        /**
         * Whether the server knows the public booking page. False while the
         * answer is unknown, so the agenda withholds the affordance rather
         * than showing a button that might lead nowhere.
         */
        bookingEnabled: computed(() => data.value?.bookingEnabled === true),
        error,
        isLoading,
        /** Refetch after a mutation; forced past swrv's 2 s deduping window. */
        refresh: () => mutate(undefined, {forceRevalidate: true})
    };
};

export default useBookingLinkPresets;
